
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const GestationTracker = () => {
  const { data: gestationData, isLoading } = useQuery({
    queryKey: ["gestation-data"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("gestation_tracking")
        .select("*")
        .eq("user_id", user.id)
        .single();
        
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Calculate week of pregnancy based on start date
  const calculateWeek = (startDate: string | undefined) => {
    if (!startDate) return 0;
    
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    
    return diffWeeks > 42 ? 42 : diffWeeks; // Cap at 42 weeks
  };

  const currentWeek = calculateWeek(gestationData?.pregnancy_start_date);
  const progressPercentage = (currentWeek / 40) * 100;
  
  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <Card>
          <CardHeader>
            <div className="h-7 bg-muted rounded w-1/3"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-6 bg-muted rounded w-1/4"></div>
            <div className="h-12 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!gestationData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aucune donnée de grossesse</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Veuillez ajouter vos informations de grossesse pour commencer le suivi.
          </p>
          <button
            className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
          >
            Ajouter mes informations
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-primary/20">
        <div className="h-2 bg-gradient-to-r from-pink-200 to-pink-400"></div>
        <CardHeader>
          <CardTitle>Semaine {currentWeek} de votre grossesse</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Semaine {currentWeek}</span>
              <span>Semaine 40</span>
            </div>
            <Progress value={progressPercentage} className="h-3" />
          </div>
          
          <div className="rounded-lg bg-pink-50 dark:bg-pink-900/20 p-4 border border-pink-100 dark:border-pink-800/30">
            <h3 className="font-medium mb-2">Développement du bébé cette semaine</h3>
            <p className="text-sm text-muted-foreground">
              {currentWeek < 13 ? (
                "Votre bébé est en plein développement des organes vitaux. Son cœur bat déjà !"
              ) : currentWeek < 28 ? (
                "Votre bébé grandit rapidement. Il peut maintenant entendre votre voix et réagir aux sons extérieurs."
              ) : (
                "Votre bébé prend du poids et se prépare pour la naissance. Ses poumons finissent de se développer."
              )}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 border border-blue-100 dark:border-blue-800/30">
              <h4 className="text-sm font-medium">Date prévue d'accouchement</h4>
              <p className="text-lg font-semibold">
                {/* Using next_checkup_date instead of due_date */}
                {gestationData.next_checkup_date ? new Date(gestationData.next_checkup_date).toLocaleDateString('fr-FR') : "Non définie"}
              </p>
            </div>
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 border border-green-100 dark:border-green-800/30">
              <h4 className="text-sm font-medium">Prochain rendez-vous</h4>
              <p className="text-lg font-semibold">
                {gestationData.next_checkup_date ? new Date(gestationData.next_checkup_date).toLocaleDateString('fr-FR') : "Non planifié"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
